import React, {
  Children,
  cloneElement,
  forwardRef,
  Fragment,
  HTMLAttributes,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react';
import { ClassNames, css } from '@emotion/react';
import { CSSValueWithLength, size as sizeStyle } from '../../styles';
import { getInputHorizontalPaddingBySize } from '../input/styles';
import { INPUT_GROUP_CLASS_NAME, INPUT_GROUP_ITEM_CLASS_NAME } from './constants';
import { InputGroupContext, InputGroupContextValue, useInputGroupContext } from './InputGroupContext';
import { InputGroupDivider } from './InputGroupDivider';
import {
  getInputGroupDividerColor,
  getInputGroupInnerSpacing,
  getInputGroupSegmentStyle,
  getInputGroupSizeStyle,
  input_group_base_css,
  input_group_default_style,
  input_group_disabled_style,
  input_group_error_style,
  input_group_highlighted_content_css,
  input_group_highlighted_style,
  input_group_nested_css,
} from './styles';
import { InputGroupDividerVariant, InputGroupSize, InputGroupStatus } from './types';

export interface InputGroupProps extends Omit<HTMLAttributes<HTMLDivElement>, 'size'> {
  /**
   * @default 'medium'
   */
  size?: InputGroupSize;
  /**
   * 필드 테두리 색을 지정합니다. 경고 아이콘이 필요하면 각 세그먼트에 `status`를 지정해주세요.
   */
  status?: InputGroupStatus;
  disabled?: boolean;
  width?: CSSValueWithLength;
  flex?: React.CSSProperties['flex'];
  /**
   * 칸을 나누는 구분선의 표현을 지정합니다. `none`이면 구분선을 그리지 않습니다.
   * @default 'default'
   */
  dividerVariant?: InputGroupDividerVariant;
  /**
   * 부모 `InputGroup`의 `dividerVariant`를 자기 `dividerVariant`보다 우선합니다.
   *
   * `Input`의 prefix/suffix가 홀로 있을 때는 PDS 1.0 표현(`full`)을 유지하면서,
   * 그룹 안에 들어가면 그룹의 표현을 따라가도록 하기 위한 내부용입니다.
   * @default false
   */
  _unstable_inheritsDividerVariant?: boolean;
}

/** 부모 그룹이 자식에게 물려줄 수 있는 prop들 */
interface SegmentOwnProps {
  className?: string;
  size?: InputGroupSize;
  disabled?: boolean;
  width?: CSSValueWithLength;
}

const flattenSegments = (children: ReactNode, prefix = ''): Array<ReactElement<SegmentOwnProps>> =>
  Children.toArray(children)
    .filter(isValidElement)
    .flatMap((child, index) => {
      const key = `${prefix}${child.key ?? index}`;

      if (child.type === Fragment) {
        return flattenSegments((child.props as { children?: ReactNode }).children, `${key}/`);
      }

      return [cloneElement(child as ReactElement<SegmentOwnProps>, { key })];
    });

export const InputGroup = forwardRef<HTMLDivElement, InputGroupProps>(
  (
    {
      className,
      size: sizeProp,
      status: statusProp,
      disabled: disabledProp,
      width,
      flex,
      dividerVariant: dividerVariantProp,
      _unstable_inheritsDividerVariant = false,
      children,
      ...props
    },
    ref,
  ) => {
    const parent = useInputGroupContext();
    const isNested = parent !== null;
    const size = sizeProp ?? parent?.size ?? 'medium';
    const status = statusProp ?? parent?.status;
    const disabled = (disabledProp ?? false) || (parent?.disabled ?? false);
    const atFieldStart = parent?.atFieldStart ?? true;
    const atFieldEnd = parent?.atFieldEnd ?? true;
    const dividerVariant = _unstable_inheritsDividerVariant
      ? parent?.dividerVariant ?? dividerVariantProp ?? 'default'
      : dividerVariantProp ?? parent?.dividerVariant ?? 'default';
    const dividerColor = getInputGroupDividerColor({ status, disabled, dividerVariant });
    const innerSpacing = getInputGroupInnerSpacing({ size, dividerVariant });
    const startSpacing = parent?.startSpacing ?? getInputHorizontalPaddingBySize(size);
    const endSpacing = parent?.endSpacing ?? getInputHorizontalPaddingBySize(size);

    const segments = flattenSegments(children);
    const lastIndex = segments.length - 1;

    const fieldStyle = [
      input_group_default_style,
      status === 'highlighted' && input_group_highlighted_style,
      status === 'error' && input_group_error_style,
      disabled && input_group_disabled_style,
    ];

    return (
      <ClassNames>
        {({ css: toClassName, cx }) => (
          <div
            role='group'
            className={cx(INPUT_GROUP_CLASS_NAME, className)}
            css={[
              input_group_base_css,
              isNested && input_group_nested_css,
              !isNested && getInputGroupSizeStyle(size),
              !isNested && fieldStyle,
              status === 'highlighted' && input_group_highlighted_content_css,
              sizeStyle({ width }),
              flex !== undefined &&
                css`
                  flex: ${flex};
                `,
            ]}
            ref={ref}
            {...props}
          >
            {segments.map((child, index) => {
              const atStart = atFieldStart && index === 0;
              const atEnd = atFieldEnd && index === lastIndex;
              const inherited: SegmentOwnProps =
                typeof child.type === 'string'
                  ? {}
                  : { size: child.props.size ?? size, ...(disabled ? { disabled: true } : null) };
              const segmentStartSpacing = index === 0 ? startSpacing : innerSpacing;
              const segmentEndSpacing = index === lastIndex ? endSpacing : innerSpacing;
              const contextValue: InputGroupContextValue = {
                size,
                disabled,
                status,
                dividerVariant,
                startSpacing: segmentStartSpacing,
                endSpacing: segmentEndSpacing,
                atFieldStart: atStart,
                atFieldEnd: atEnd,
              };

              return (
                <Fragment key={child.key ?? index}>
                  {index > 0 && dividerVariant !== 'none' && (
                    <InputGroupDivider size={size} variant={dividerVariant} color={dividerColor} />
                  )}
                  <InputGroupContext.Provider value={contextValue}>
                    {cloneElement(child, {
                      ...inherited,
                      className: cx(
                        child.props.className,
                        INPUT_GROUP_ITEM_CLASS_NAME,
                        toClassName(
                          getInputGroupSegmentStyle({
                            startSpacing: segmentStartSpacing,
                            endSpacing: segmentEndSpacing,
                            atFieldStart: atStart,
                            atFieldEnd: atEnd,
                            hasOwnWidth: child.props.width !== undefined,
                          }),
                        ),
                      ),
                    })}
                  </InputGroupContext.Provider>
                </Fragment>
              );
            })}
          </div>
        )}
      </ClassNames>
    );
  },
);

InputGroup.displayName = 'InputGroup';
